/**
 * AuditLog.tsx — Complete record of every action taken on the platform.
 * Rebuilt in Pass 3.
 */

import { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { theme } from '../../theme';
import {
  PageHeader,
  Icon,
  Select,
  Table, TableToolbar, Pagination,
  Tag, TagKind,
  EmptyState,
  toast,
} from '../../components/ui';

interface AuditEntry {
  id: string;
  event_type: string;
  user_email: string | null;
  user_role: string | null;
  client_name: string | null;
  resource_type: string | null;
  resource_id: string | null;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  created_at: string;
}

// Map each event type to a semantic Tag kind so they share the design system's color rules.
const eventKind: Record<string, TagKind> = {
  login_success: 'success',
  login_failed: 'danger',
  pipeline_started: 'cyan',
  pipeline_completed: 'success',
  pipeline_failed: 'danger',
  match_approved: 'warning',
  match_rejected: 'warning',
  export_requested: 'purple',
  user_created: 'cyan',
  user_deactivated: 'danger',
};

const LIMIT = 50;

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [eventFilter, setEventFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Audit Log | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, limit: LIMIT };
    if (eventFilter) params.event_type = eventFilter;

    api.get('/api/audit/log', { params })
      .then(r => {
        setEntries(r.data.entries || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Failed to load audit log'))
      .finally(() => setLoading(false));
  }, [page, eventFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const summarizeMetadata = (m: Record<string, unknown>): string => {
    if (!m || Object.keys(m).length === 0) return '';
    const parts: string[] = [];
    if (m.client_name) parts.push(String(m.client_name));
    if (Array.isArray(m.client_names)) parts.push((m.client_names as string[]).join(', '));
    if (m.reason) parts.push(String(m.reason));
    if (m.role) parts.push(`role: ${m.role}`);
    if (m.email) parts.push(String(m.email));
    if (typeof m.duration === 'number') parts.push(`${m.duration.toFixed(1)}s`);
    if (m.format) parts.push(String(m.format));
    if (m.error) parts.push(String(m.error).slice(0, 60));
    return parts.join(' · ') || JSON.stringify(m).slice(0, 80);
  };

  const columns = [
    {
      key: 'created_at',
      header: 'Timestamp',
      mono: true,
      nowrap: true,
      render: (r: AuditEntry) => (
        <span style={{ color: theme.text.primary, fontSize: theme.fontSize.sm }}>
          {formatTime(r.created_at)}
        </span>
      ),
    },
    {
      key: 'event_type',
      header: 'Event',
      render: (r: AuditEntry) => (
        <Tag kind={eventKind[r.event_type] ?? 'neutral'} size="sm">
          {r.event_type.replace(/_/g, ' ')}
        </Tag>
      ),
      nowrap: true,
    },
    {
      key: 'user_email',
      header: 'User',
      render: (r: AuditEntry) => r.user_email
        ? <span style={{ color: theme.text.primary }}>{r.user_email}</span>
        : <span style={{ color: theme.text.tertiary, fontStyle: 'italic' }}>system</span>,
    },
    {
      key: 'client_name',
      header: 'Client',
      render: (r: AuditEntry) => r.client_name
        ? <span style={{ color: theme.text.secondary }}>{r.client_name}</span>
        : <span style={{ color: theme.text.quaternary }}>—</span>,
    },
    {
      key: 'resource',
      header: 'Resource',
      mono: true,
      render: (r: AuditEntry) => r.resource_type ? (
        <span style={{ color: theme.text.secondary, fontSize: theme.fontSize.sm }}>
          {r.resource_type}{r.resource_id ? `:${r.resource_id.slice(0, 8)}` : ''}
        </span>
      ) : <span style={{ color: theme.text.quaternary }}>—</span>,
      nowrap: true,
    },
    {
      key: 'metadata',
      header: 'Details',
      render: (r: AuditEntry) => {
        const s = summarizeMetadata(r.metadata);
        return (
          <span
            title={s}
            style={{
              color: theme.text.tertiary,
              fontSize: theme.fontSize.sm,
              display: 'inline-block',
              maxWidth: 320,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle',
            }}
          >
            {s || '—'}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ padding: `${theme.space[8]} ${theme.space[10]}`, maxWidth: 1600, margin: '0 auto' }}>
      <PageHeader
        title="Audit Log"
        description="Complete record of every action taken on the platform."
        meta={
          <>
            <span style={{ color: theme.text.primary, fontWeight: theme.fontWeight.medium }}>
              {total.toLocaleString()}
            </span>
            <span>events</span>
          </>
        }
      />

      <div style={{ display: 'flex', gap: theme.space[3], marginBottom: theme.space[5] }}>
        <div style={{ minWidth: 220 }}>
          <Select value={eventFilter} onChange={e => { setEventFilter(e.target.value); setPage(1); }}>
            <option value="">All events</option>
            <option value="login_success">Login success</option>
            <option value="login_failed">Login failed</option>
            <option value="pipeline_started">Pipeline started</option>
            <option value="pipeline_completed">Pipeline completed</option>
            <option value="pipeline_failed">Pipeline failed</option>
            <option value="match_approved">Match approved</option>
            <option value="match_rejected">Match rejected</option>
            <option value="export_requested">Export requested</option>
            <option value="user_created">User created</option>
            <option value="user_deactivated">User deactivated</option>
          </Select>
        </div>
      </div>

      <TableToolbar>
        <div style={{ color: theme.text.tertiary, fontSize: theme.fontSize.sm }}>
          Showing{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {entries.length.toLocaleString()}
          </span>
          {' '}of{' '}
          <span style={{ color: theme.text.primary, fontFamily: theme.font.data, fontWeight: theme.fontWeight.medium }}>
            {total.toLocaleString()}
          </span>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </TableToolbar>

      <Table
        data={entries}
        columns={columns}
        loading={loading}
        minWidth={1100}
        rowKey={r => r.id}
        emptyState={
          <EmptyState
            icon={<Icon.Shield size={18} />}
            title="No audit events found"
            description={eventFilter ? 'Try a different event filter.' : 'Platform activity will appear here.'}
          />
        }
      />
    </div>
  );
}
